'use client';

import * as React from 'react';
import { Columns3, GripVertical, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { cn } from '../../lib/utils';

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  locked?: boolean; // Cannot be hidden (e.g., name column)
}

export interface ColumnPickerProps {
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  triggerClassName?: string;
  contentClassName?: string;
}

export const ColumnPicker = React.forwardRef<
  HTMLDivElement,
  ColumnPickerProps
>(({ columns, onColumnsChange, triggerClassName, contentClassName }, ref) => {
  const [localColumns, setLocalColumns] = React.useState<ColumnConfig[]>(columns);
  const [draggedItem, setDraggedItem] = React.useState<string | null>(null);
  const [originalColumns] = React.useState<ColumnConfig[]>(columns);

  // Sync external changes
  React.useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  const handleToggleVisibility = (columnId: string) => {
    const updated = localColumns.map((col) =>
      col.id === columnId && !col.locked
        ? { ...col, visible: !col.visible }
        : col,
    );
    setLocalColumns(updated);
    onColumnsChange(updated);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, columnId: string) => {
    setDraggedItem(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetId: string,
  ) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = localColumns.findIndex((col) => col.id === draggedItem);
    const targetIndex = localColumns.findIndex((col) => col.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      return;
    }

    const updated = [...localColumns];
    const [draggedCol] = updated.splice(draggedIndex, 1);
    updated.splice(targetIndex, 0, draggedCol);

    setLocalColumns(updated);
    onColumnsChange(updated);
    setDraggedItem(null);
  };

  const handleReset = () => {
    setLocalColumns(originalColumns);
    onColumnsChange(originalColumns);
  };

  const visibleCount = localColumns.filter((col) => col.visible).length;

  return (
    <div ref={ref}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn('gap-2', triggerClassName)}
          >
            <Columns3 className="h-4 w-4" />
            Columns
            {visibleCount < localColumns.length && (
              <span className="ml-1 text-xs font-medium text-[var(--color-muted-foreground)]">
                ({visibleCount})
              </span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className={cn('w-64 p-0', contentClassName)}
          align="end"
          side="bottom"
        >
          {/* Header */}
          <div className="border-b border-[var(--color-border)] px-4 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">
              Columns
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="h-7 px-2 gap-1"
              title="Reset to default column visibility"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span className="text-xs">Reset</span>
            </Button>
          </div>

          {/* Column List */}
          <div className="max-h-80 overflow-y-auto">
            {localColumns.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[var(--color-muted-foreground)]">
                No columns available
              </div>
            ) : (
              <div className="divide-y divide-[var(--color-border)]">
                {localColumns.map((column) => (
                  <div
                    key={column.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, column.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, column.id)}
                    className={cn(
                      'group flex items-center gap-3 px-4 py-3 cursor-move transition-colors',
                      draggedItem === column.id
                        ? 'bg-[var(--color-muted)] opacity-50'
                        : 'hover:bg-[var(--color-muted)]',
                    )}
                  >
                    {/* Drag Handle */}
                    <div className="flex-shrink-0 text-[var(--color-muted-foreground)] opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="h-4 w-4" />
                    </div>

                    {/* Checkbox */}
                    <Checkbox
                      id={`column-${column.id}`}
                      checked={column.visible}
                      onCheckedChange={() => handleToggleVisibility(column.id)}
                      disabled={column.locked}
                      className={cn(
                        'flex-shrink-0',
                        column.locked && 'cursor-not-allowed opacity-60',
                      )}
                    />

                    {/* Column Label */}
                    <label
                      htmlFor={`column-${column.id}`}
                      className={cn(
                        'flex-1 text-sm cursor-pointer select-none',
                        column.visible
                          ? 'text-[var(--color-foreground)] font-medium'
                          : 'text-[var(--color-muted-foreground)]',
                        column.locked && 'cursor-not-allowed',
                      )}
                    >
                      {column.label}
                    </label>

                    {/* Visibility Icon */}
                    <div className="flex-shrink-0 text-[var(--color-muted-foreground)]">
                      {column.visible ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[var(--color-border)] px-4 py-3 text-xs text-[var(--color-muted-foreground)]">
            Drag to reorder • Uncheck to hide
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});

ColumnPicker.displayName = 'ColumnPicker';
